import * as Sentry from "@sentry/react";
import { StartClient } from "@tanstack/react-start/client";
import { startTransition } from "react";
import { hydrateRoot } from "react-dom/client";
import { initDomNodeErrorGuard } from "./lib/dom-node-error-handler";
import { didLocaleSwitchAfterHydration } from "./lib/locale";
import { initPreloadErrorHandler } from "./lib/preload-error-handler";
import { initVersionCheck } from "./lib/version-check";

initPreloadErrorHandler();
initVersionCheck();
// Neutralize translation/extension-induced `insertBefore`/`removeChild` crashes
// before the first React commit. Must precede `hydrateRoot`.
initDomNodeErrorGuard();

// React가 스스로 복구한 hydration mismatch(#418/#423/#425 — 서버 HTML과 다른 텍스트,
// 경계를 클라이언트 렌더로 재생성). 기본 동작은 reportError → 전역 onerror → Sentry 에러.
const HYDRATION_ERROR_RE =
	/hydrat|Minified React error #(?:418|423|425)\b|did not match|does not match/i;

// 프레임워크 기본 엔트리(react-start/src/default-entry/client.tsx)와 동일 구조.
// 델타: StrictMode 미사용(기존 main.tsx와 동일) + Sentry 에러 핸들러 장착.
// Sentry init 자체는 라우터 인스턴스가 필요해 getRouter()(src/router.tsx)에서 수행.
startTransition(() => {
	hydrateRoot(document, <StartClient />, {
		onUncaughtError: Sentry.reactErrorHandler(),
		onCaughtError: Sentry.reactErrorHandler(),
		onRecoverableError: (error, errorInfo) => {
			const message = error instanceof Error ? error.message : String(error);
			// 예상된 복구: LocaleSync가 SSR 로케일(URL)과 다른 선호 로케일로 전환한 뒤 늦게
			// hydrate된 경계 — 결과는 SPA 시절과 동일(클라이언트 렌더), 노이즈만 제거한다.
			// 전환이 없었는데 나는 mismatch는 진짜 SSR 버그 → 경고 레벨로 수집.
			if (HYDRATION_ERROR_RE.test(message) && didLocaleSwitchAfterHydration()) {
				return;
			}
			Sentry.captureException(error, {
				level: "warning",
				tags: { source: "react-recoverable" },
				contexts: { react: { componentStack: errorInfo.componentStack } },
			});
		},
	});
});
